import React, { useEffect, useState, useRef } from "react";
import api from "../../lib/api";
import { useAdminPref, ActiveOnlySwitch } from "../../lib/adminPrefs";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, FilePdf } from "@phosphor-icons/react";
import { PageHeader, SectionPad, Badge } from "../../components/ui-extras";
import { Can } from "../../lib/rbac";

const empty = {
  company_name: "", contact_name: "", contact_email: "", phone: "", address: "",
  // P4-NEW-CUSTOMER-ADDRESS-FIELD-PARITY R1 (2026-XX). Additive structured
  // postal-address fields already supported by CustomerIn / CustomerUpdate
  // and by the Edit Customer modal in CustomerDetail.jsx (PROFILE section).
  // Canonical field names match the backend model exactly — no
  // transformation, no auto-parse of legacy free-text `address`.
  city: "", province: "ON", postal_code: "", country: "",
  billing_email: "", billing_days: ["mon"], tax_rate: 0.13,
  credit_limit: 0, currency: "CAD", status: "active", non_listed_surcharge_cpl: 5.0,
};

export default function Customers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [activeOnly, setActiveOnly] = useAdminPref("customers_active_only");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(empty);
  const [showImport, setShowImport] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [selectedIdx, setSelectedIdx] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const fileRef = useRef();

  const load = async () => {
    const { data } = await api.get("/customers");
    setRows(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = rows.filter((r) => (!activeOnly || r.status === "active")).filter((r) =>
    !q || r.company_name.toLowerCase().includes(q.toLowerCase()) || (r.province || "").toLowerCase().includes(q.toLowerCase())
  );

  const save = async () => {
    try {
      await api.post("/customers", form);
      toast.success("Customer created");
      setShowCreate(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <SectionPad>
      <PageHeader
        title="Customers"
        subtitle="DIRECTORY / CUSTOMERS"
        testId="customers-header"
        actions={
          <>
            <Can perm="customers.edit">
            <button
              className="gf-btn gf-btn-outline"
              onClick={() => { setShowImport(true); setExtracted(null); setSelectedIdx(new Set()); }}
              data-testid="import-customers-button"
            >
              <FilePdf size={14} /> Import from invoices
            </button>
            <button className="gf-btn gf-btn-primary" onClick={() => setShowCreate(true)} data-testid="add-customer-button">
              <Plus size={14} /> Add customer
            </button>
            </Can>
          </>
        }
      />

      <div className="gf-card">
        <div className="gf-card-header">
          <div className="flex items-center gap-2" style={{ flex: 1 }}>
            <MagnifyingGlass size={16} className="text-zinc-400" />
            <input
              placeholder="Search by company or province…"
              value={q} onChange={(e) => setQ(e.target.value)}
              className="border-0 outline-none text-sm flex-1"
              style={{ background: "transparent" }}
              data-testid="customers-search"
            />
          </div>
          <div className="mr-4">
            <ActiveOnlySwitch checked={activeOnly} onChange={setActiveOnly} testId="customers-active-only-toggle" />
          </div>
          <div className="overline" data-testid="customers-results-count">{filtered.length} RESULTS</div>
        </div>
        <div className="overflow-x-auto">
          <table className="gf-table">
            <thead>
              <tr>
                <th>COMPANY</th>
                <th>PROV</th>
                <th>BILLING</th>
                <th>TAX</th>
                <th className="text-right">CREDIT</th>
                <th className="text-right">AR BALANCE</th>
                <th>STATUS</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.customer_id} data-testid={`customer-row-${r.customer_id}`}>
                  <td>
                    <Link to={`/customers/${r.customer_id}`} className="font-medium hover:underline">{r.company_name}</Link>
                    <div className="text-xs text-zinc-500">{r.contact_email}</div>
                  </td>
                  <td className="font-mono">{r.province}</td>
                  <td className="font-mono text-xs">{(r.billing_days || []).join(",").toUpperCase()}</td>
                  <td className="font-mono">{(r.tax_rate * 100).toFixed(2)}%</td>
                  <td className="gf-num">${(r.credit_limit || 0).toLocaleString()}</td>
                  <td className="gf-num">${(r.ar_balance || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                  <td><Badge variant={r.status === "active" ? "success" : "muted"}>{r.status}</Badge></td>
                  <td className="text-right">
                    <Link to={`/customers/${r.customer_id}`} className="gf-btn gf-btn-ghost" data-testid={`customer-open-${r.customer_id}`}>Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white w-full max-w-lg border border-zinc-200" onClick={(e) => e.stopPropagation()}>
            <div className="gf-card-header"><h3>New customer</h3></div>
            <div className="gf-card-body space-y-3">
              {[
                ["company_name", "Company name"],
                ["contact_name", "Contact name"],
                ["contact_email", "Contact email"],
                ["billing_email", "Billing email"],
                ["phone", "Phone"],
                ["address", "Street address"],
                ["city", "City"],
                ["province", "Province"],
                ["postal_code", "Postal / ZIP code"],
                ["country", "Country"],
              ].map(([k, l]) => (
                <div key={k}>
                  <div className="overline mb-1">{l}</div>
                  <input className="gf-input" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`input-${k}`} />
                </div>
              ))}
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="overline mb-1">Tax rate</div>
                  <input type="number" step="0.01" className="gf-input" value={form.tax_rate} onChange={(e) => setForm({ ...form, tax_rate: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <div className="overline mb-1">Credit limit</div>
                  <input type="number" className="gf-input" value={form.credit_limit} onChange={(e) => setForm({ ...form, credit_limit: parseFloat(e.target.value) || 0 })} />
                </div>
                <div>
                  <div className="overline mb-1">Surcharge CPL</div>
                  <input type="number" step="0.1" className="gf-input" value={form.non_listed_surcharge_cpl} onChange={(e) => setForm({ ...form, non_listed_surcharge_cpl: parseFloat(e.target.value) || 0 })} />
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button className="gf-btn gf-btn-outline" onClick={() => setShowCreate(false)}>Cancel</button>
              <button className="gf-btn gf-btn-primary" onClick={save} data-testid="save-customer-button">Create</button>
            </div>
          </div>
        </div>
      )}

      {showImport && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !extracting && !importing && setShowImport(false)}
        >
          <div
            className="bg-white w-full max-w-5xl border border-zinc-200 max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="gf-card-header">
              <h3>Import customers from invoice PDFs</h3>
              <div className="overline">AI-POWERED · CLAUDE SONNET</div>
            </div>

            <div className="gf-card-body overflow-y-auto flex-1">
              {!extracted ? (
                <>
                  <div className="text-sm text-zinc-600 mb-3">
                    Upload a PDF containing one or many customer invoices. The AI will read every page
                    and extract unique customer billing records (company name, address, email, phone, tax rate, province).
                    You'll preview the list before anything is saved.
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="application/pdf,.pdf"
                    multiple
                    className="gf-input"
                    data-testid="invoice-pdf-input"
                  />
                  <div className="text-xs text-zinc-500 mt-3">
                    Drop one PDF or many — each file is run through extraction
                    sequentially and the results are merged into one preview.
                    Duplicate company names across files are deduped automatically.
                  </div>
                </>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="overline">EXTRACTED {extracted.count} CUSTOMERS</div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {selectedIdx.size} selected · duplicates highlighted and skipped
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        className="gf-btn gf-btn-outline"
                        onClick={() => {
                          const all = new Set();
                          extracted.customers.forEach((c, i) => { if (!c.is_duplicate) all.add(i); });
                          setSelectedIdx(all);
                        }}
                        data-testid="select-all-customers"
                      >
                        Select all new
                      </button>
                      <button className="gf-btn gf-btn-outline" onClick={() => setSelectedIdx(new Set())}>
                        Clear
                      </button>
                    </div>
                  </div>
                  <div className="overflow-x-auto border border-zinc-200">
                    <table className="gf-table">
                      <thead>
                        <tr>
                          <th style={{ width: 36 }}></th>
                          <th>COMPANY</th>
                          <th>EMAIL</th>
                          <th>PHONE</th>
                          <th>PROV</th>
                          <th>TAX %</th>
                          <th>CURR</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {extracted.customers.map((c, i) => (
                          <tr key={i} style={c.is_duplicate ? { background: "#FEF9C3" } : {}}>
                            <td>
                              <input
                                type="checkbox"
                                disabled={c.is_duplicate}
                                checked={selectedIdx.has(i)}
                                onChange={(e) => {
                                  const n = new Set(selectedIdx);
                                  if (e.target.checked) n.add(i); else n.delete(i);
                                  setSelectedIdx(n);
                                }}
                                data-testid={`select-customer-${i}`}
                              />
                            </td>
                            <td>
                              <div className="font-medium text-sm">{c.company_name}</div>
                              <div className="text-xs text-zinc-500">{c.address}</div>
                            </td>
                            <td className="font-mono text-xs">{c.contact_email || "—"}</td>
                            <td className="font-mono text-xs">{c.phone || "—"}</td>
                            <td className="font-mono">{c.province || "—"}</td>
                            <td className="font-mono">{(c.tax_rate * 100).toFixed(1)}%</td>
                            <td className="font-mono text-xs">{c.currency}</td>
                            <td>
                              {c.is_duplicate && <Badge variant="warning">DUPLICATE</Badge>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>

            <div className="flex justify-end gap-2 p-4 border-t border-zinc-200">
              <button
                className="gf-btn gf-btn-outline"
                onClick={() => { setShowImport(false); setExtracted(null); }}
                disabled={extracting || importing}
              >
                Close
              </button>
              {!extracted ? (
                <button
                  className="gf-btn gf-btn-primary"
                  disabled={extracting}
                  data-testid="extract-customers-button"
                  onClick={async () => {
                    const files = Array.from(fileRef.current?.files || []);
                    if (files.length === 0) return toast.error("Choose one or more PDF files");
                    setExtracting(true);
                    try {
                      // Run extraction sequentially. The backend prompt is
                      // tuned for a single merged PDF; running per-file keeps
                      // memory + timeout pressure on the API low and lets us
                      // surface a per-file error without losing earlier results.
                      const allCustomers = [];
                      const seen = new Map();   // company_name (uppercased) → index in allCustomers
                      let okFiles = 0, failFiles = 0;
                      for (const f of files) {
                        try {
                          const fd = new FormData();
                          fd.append("file", f);
                          const { data } = await api.post("/customers/extract-from-invoices", fd, {
                            headers: { "Content-Type": "multipart/form-data" },
                            timeout: 180000,
                          });
                          for (const c of (data.customers || [])) {
                            const key = (c.company_name || "").trim().toUpperCase();
                            if (!key) continue;
                            if (seen.has(key)) continue;  // cross-file dedupe by name
                            seen.set(key, allCustomers.length);
                            allCustomers.push({ ...c, _source_file: f.name });
                          }
                          okFiles += 1;
                        } catch (err) {
                          failFiles += 1;
                          toast.error(`${f.name}: ${err.response?.data?.detail || "extraction failed"}`);
                        }
                      }
                      const merged = { count: allCustomers.length, customers: allCustomers };
                      setExtracted(merged);
                      const n = new Set();
                      allCustomers.forEach((c, i) => { if (!c.is_duplicate) n.add(i); });
                      setSelectedIdx(n);
                      const summary =
                        files.length === 1
                          ? `Found ${merged.count} unique customer(s)`
                          : `Found ${merged.count} unique customer(s) across ${okFiles} of ${files.length} file(s)`;
                      toast.success(summary);
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Extraction failed");
                    } finally {
                      setExtracting(false);
                    }
                  }}
                >
                  {extracting ? "Extracting…" : "Extract customers"}
                </button>
              ) : (
                <button
                  className="gf-btn gf-btn-primary"
                  disabled={importing || selectedIdx.size === 0}
                  data-testid="import-customers-confirm"
                  onClick={async () => {
                    setImporting(true);
                    try {
                      const picked = [...selectedIdx].map((i) => extracted.customers[i]);
                      const { data } = await api.post("/customers/bulk-create", { customers: picked });
                      toast.success(`Imported ${data.created} · skipped ${data.skipped}`);
                      setShowImport(false);
                      setExtracted(null);
                      setSelectedIdx(new Set());
                      load();
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Import failed");
                    } finally {
                      setImporting(false);
                    }
                  }}
                >
                  {importing ? "Importing…" : `Import ${selectedIdx.size} customer(s)`}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </SectionPad>
  );
}
